import { CountCard } from "../../../../components/CountCard/CountCard";
import Empty from "../../../../components/EmptyPagination/Empty";
import usePagination from "src/hooks/usePagination";
import { JobApplicationStatus } from "src/types";
import PaginatedDiv from "src/components/PaginatedDiv";
import Table from "src/components/Tables/Table";
import Button from "src/components/Button";
import { InterviewMetrics } from "src/network/interview/type";
import { useMemo, useState } from "react";
import useSlugParam from "src/hooks/useSlug";
import LoaderPage from "src/components/Loader/LoaderPage";
import { dateFormat1 } from "src/utils/dates/formats";
import { interviewInvite } from "src/network/interview";
import { customToast } from "src/utils/toast";
import { PaginatedJobApplicationParams } from "src/network/job_application/type";
import Tabs from "src/components/Tabs";
import { useAppSelector } from "src/redux/store";
import ConstraintModal from "src/components/Modal/ConstraintModal";
import { AtsJobApplication } from "src/types/models/AtsJobApplication";
import { getAtsJobsApplicationsPaginated } from "src/network/ats_job_applications";
import DeclineAtsApplicationModal from "../components/DeclineAtsApplicationModal";

type tab = "New Applicants" | "Rejected Applicants";
const tabs: tab[] = ["New Applicants", "Rejected Applicants"];
const tabStatus: { [key in tab]: JobApplicationStatus } = {
  "New Applicants": "APPLIED",
  "Rejected Applicants": "DECLINED"
};

const headers = ["Name | Email Address", "Role", "Date Applied", "Action"];

const AtsTalents = ({ metrics }: { metrics?: InterviewMetrics }) => {
  const intMonthLimit = useAppSelector(
    (r) => !!r.client?.client?.Company.UsageConstraints?.completed_monthly_job_interviews_exceeded
  );
  const [inviteLimit, setInviteLimit] = useState(false);
  const { id } = useSlugParam();
  const [filters, setFilters] = useState<PaginatedJobApplicationParams>({
    job_id: id,
    status: "APPLIED"
  });
  const heads = useMemo(
    () => (filters.status === "APPLIED" ? headers : headers.slice(0, -1)),
    [filters]
  );
  const { data, count, fetching, fetch, refresh, removeData } = usePagination<AtsJobApplication>({
    fetchPage: getAtsJobsApplicationsPaginated,
    page_size: 10,
    filters
  });
  // Reminder email handling
  const [inviting, setInviting] = useState<number>();
  const invite = async (a: AtsJobApplication) => {
    if (intMonthLimit) {
      setInviteLimit(true);
      return;
    }
    if (!id) return;
    setInviting(a.id);
    try {
      const interview = await interviewInvite({
        job_id: id,
        data: {
          first_name: a.first_name || "",
          last_name: a.last_name || "",
          email: a.email || ""
        }
      });
      if (!interview) throw new Error("No invite happened");
      const app = data?.find((app) => app.id === a.id);
      if (app) app.JobInterview = interview;
      refresh(); // To reload the changed app
    } catch (e) {
      console.error(e);
      customToast({ message: "Error occured sending invite" });
    }
    setInviting(undefined);
  };

  // Decliner handler
  const [declining, setDeclining] = useState<number>();
  const decline = () => {
    removeData((a) => a.id !== declining);
    setDeclining(undefined);
  };

  return (
    <>
      <div className="flex gap-16 mb-6">
        <CountCard color="green" title="Interviewed" count={metrics?.COMPLETED} />
        <CountCard color="yellow" title="Invited" count={metrics?.INVITED} />
        <CountCard color="green" title="Shorlisted" count={metrics?.PASSED} />
      </div>

      <Tabs
        style={1}
        className="mb-6"
        tabs={tabs}
        onChange={(s) => setFilters({ ...filters, status: tabStatus[s] })}
      />

      {count === 0 && (
        <Empty
          title="There are no applicants yet"
          text="It will be available once talent start applying"
        />
      )}

      {!data && <LoaderPage />}

      {data && !!count && (
        <PaginatedDiv
          className="!h-auto flex-1 "
          onLimit={fetch}
          loading={fetching}
          count={count}
          currentCount={data?.length}
        >
          <Table
            data={data}
            className="overflow-x-scroll"
            heads={heads}
            row={(a) => {
              const { id, applied_at, JobInterview, status, email, full_name, title } =
                a as AtsJobApplication;
              const declined = status === "DECLINED";
              const invited = !!JobInterview;
              return [
                <div>
                  <p>{full_name}</p>
                  <p className="max-w-[207px] overflow-hidden text-ellipsis mt-2">{email}</p>
                </div>,
                title || "-",
                applied_at ? dateFormat1(applied_at) : "",
                <div className="flex gap-4">
                  {!declined && (
                    <Button
                      onClick={() => invite(a)}
                      loading={inviting === id}
                      variant="white_blue"
                      className="!text-sm !rounded-3xl !w-fit !px-4"
                      disabled={invited || !!inviting}
                    >
                      {invited ? "Invited" : "Invite"}
                    </Button>
                  )}
                  {!invited && !declined && (
                    <Button
                      disabled={!!inviting}
                      onClick={() => setDeclining(a.id)}
                      className="!text-sm !rounded-3xl !w-fit !px-4"
                      variant="white_red"
                    >
                      Decline
                    </Button>
                  )}
                </div>
              ];
            }}
          />
        </PaginatedDiv>
      )}

      {/* Modals */}
      <DeclineAtsApplicationModal
        id={declining}
        close={() => setDeclining(undefined)}
        onDecline={decline}
      />

      <ConstraintModal
        open={inviteLimit}
        variant="month_int_limit"
        onClose={() => setInviteLimit(false)}
      />
    </>
  );
};

export default AtsTalents;
